import { useState, useEffect } from 'react';

import GenericAccordian from '../../components/GenericAccordian/GenericAccordian';
import { COUNTRY_CODE, USERS_ENTITY, BUCKET_MANAGEMENT } from '../../constants/EntityName';
import { getFormDefaultValue, getTableHeaders } from '../../utils/forms/FormDataModel';
import { ContentItem, OnAddArguments, TabContent } from '../../utils/forms/FormInterface';
import AbstractForm from '../Entity/AbstractForm';
import AbstractLanding from '../Entity/AbstractLanding';
import AbstractTabs, { TabActiosEventType, TabActiosProps } from '../Entity/AbstractTabs';
import { usersFormData } from '../../datamodels/Users';
import { getOptions } from '../../datamodels/OptionService';

const additionalEntityName = [{ name: COUNTRY_CODE, getAllResponse: true }];

const UsersPage = () => {
  const [activeKey, setActiveKey] = useState<string>('0');
  const [userName, setUserName] = useState<string>();
  const [showFormComponent, setShowFormComponent] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [content, setContent] = useState<TabContent>([]);
  const [formData, setFormData] = useState(usersFormData);

  const defaultFormValue: any = getFormDefaultValue(formData);
  const tableHeaders: any = getTableHeaders(formData);
  const addName = 'User';
  const headerName = 'Users';

  // Fetch active bucket options when component mounts
  useEffect(() => {
    const loadBucketOptions = async () => {
      try {
        const options = await getOptions(`${BUCKET_MANAGEMENT}?activeOnly=true`);
        const tempArr = options.map((item: any) => ({
          name: item.title,
          value: item.title,
          id: item.id,
          label: item.title,
        }));

        if (tempArr.length > 0) {
          const updatedFormData = usersFormData.map((item: any) => {
            if (item.name === 'bucket') {
              return {
                ...item,
                fieldType: {
                  ...item.fieldType,
                  options: tempArr,
                },
              };
            }
            return item;
          });
          setFormData(updatedFormData);
        }
      } catch (error) {
        console.error('Error loading bucket options:', error);
      }
    };

    loadBucketOptions();
  }, []);

  const onSuccessful = (id?: string) => {
    if (id === 'create') {
      setShowFormComponent(false);
    }
    onTabClick('0');
    setRefreshData(prev => !prev);
  };

  const onAddNew = (argument1?: OnAddArguments) => {
    if (argument1) {
      const checkArr = content.filter((item: ContentItem) => item.key === argument1.id);
      checkArr.length ? null : onAdd(argument1);
    } else {
      setShowFormComponent(true);
      setUserName('Add User');
      onTabClick('a');
    }
  };

  const closeTab = (key: string, shouldRefresh: boolean) => {
    setContent(content => content.filter(item => item.key !== key));
    setActiveKey('0');
    shouldRefresh && setRefreshData(prev => !prev);
    key === 'a' && setShowFormComponent(false);
  };

  const onAdd = (argument1: OnAddArguments) => {
    const newObj = {
      key: argument1.id,
      tabTitle: argument1.name,
      content: (
        <GenericAccordian key={content.length}>
          <AbstractForm
            entityId={argument1.id}
            isCreateMode={false}
            headerName={headerName}
            entityName={USERS_ENTITY}
            formTemplate={formData}
            formDefaultPayload={getFormDefaultValue(formData)}
            onSuccessfulUpdate={onSuccessful}
            formHeader=""
            showDeleteDraft={true}
            closeTab={closeTab}
            additionalEntityName={additionalEntityName}
          />
        </GenericAccordian>
      ),
    };

    // It is to make the newly added tab as active
    setContent((prev: any) => [...prev, newObj]);
    argument1.id && setActiveKey(String(argument1.id));
  };

  const onTabClick = (key: string) => {
    setActiveKey(key);
  };

  const updateFormVisibility = (
    action: TabActiosProps,
    targetKey?: string | TabActiosEventType
  ) => {
    if (action === 'remove') {
      if (targetKey === 'a') {
        setShowFormComponent(false);
        onTabClick('0');
      } else {
        onTabClick('0');
        // whenever a tab is closed, that tab is filtered out from the list of tabs which are rendered inside the AbstractTab component
        setContent(content => content.filter(item => String(item.key) !== targetKey));
      }
    }
  };

  return (
    <AbstractTabs
      activeKey={activeKey}
      entityName="Admin Users"
      updateFormVisibility={updateFormVisibility}
      onTabClick={onTabClick}
      secondTabTitle={userName}
      showFormComponent={showFormComponent}
      content={content}
      formComponent={
        <GenericAccordian>
          <AbstractForm
            isCreateMode={true}
            headerName={headerName}
            entityName={USERS_ENTITY}
            formTemplate={formData}
            formDefaultPayload={defaultFormValue}
            onSuccessfulUpdate={onSuccessful}
            closeTab={closeTab}
            additionalEntityName={additionalEntityName}
          />
        </GenericAccordian>
      }
    >
      <AbstractLanding
        headerName={headerName}
        addName={addName}
        entityName={USERS_ENTITY}
        tableHeaders={tableHeaders}
        onAdd={onAddNew}
        refreshData={refreshData}
      />
    </AbstractTabs>
  );
};

export default UsersPage;
